import { Divider, Group, Stack, Text } from '@mantine/core';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Panel } from 'components/index';
import { tKeys, useTranslation } from 'locales';
import { useLoadUnhandledClient } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useId } from 'react';
import { prop, sumBy } from 'remeda';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import type { ThirdPartyCaseTypes } from 'types/third-party-case-types';

export const ClientSnapshot = () => {
  const { t } = useTranslation();
  const debtor = useDebtor();

  const { unhandledClient } = useLoadUnhandledClient({ debtorId: String(debtor.id) });
  const { underActiveRecoveryInvoices, paymentPlanInvoices, notDueInvoices } = unhandledClient;

  const formatter = useLocalizedCurrencyFormatter();

  const underActiveRecoveryTotal = sumBy(underActiveRecoveryInvoices, prop('totalTvac'));
  const paymentPlanTotal = sumBy(paymentPlanInvoices, prop('totalTvac'));
  const notDueTotal = sumBy(notDueInvoices, prop('totalTvac'));

  const caseTypeSubtitle = (debtor.opened_case_types ?? []).map(translateCaseType(t)).join(', ');

  return (
    <Panel pt={12}>
      <Stack gap={0}>
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.UNDER_ACTIVE_RECOVERY)}
          value={t(tKeys.INVOICE_COUNT, { count: underActiveRecoveryInvoices.length })}
          subtitle={formatter.format(underActiveRecoveryTotal)}
        />
        <Divider />
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.NOT_DUE_YET)}
          value={t(tKeys.INVOICE_COUNT, { count: notDueInvoices.length })}
          subtitle={formatter.format(notDueTotal)}
        />
        <Divider />
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.AVG_PAYMENT_DELAY)}
          value={t(tKeys.DAYS, { count: debtor.average_payment_delay })}
        />
        <Divider />
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.INVOICES_PAID_LATE)}
          value={`${Math.round(debtor.delay_rate)}%`}
        />
        <Divider />
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.THIRD_PARTY_CASES)}
          value={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.OPEN, {
            count: debtor.opened_cases_count,
          })}
          subtitle={caseTypeSubtitle}
        />
        <Divider />
        <SnapshotRow
          label={t(tKeys.MODULES.AT_RISK.CLIENT_SNAPSHOT.IN_PAYMENT_PLAN)}
          value={t(tKeys.INVOICE_COUNT, { count: paymentPlanInvoices.length })}
          subtitle={formatter.format(paymentPlanTotal)}
        />
        <Divider />
      </Stack>
    </Panel>
  );
};

// Maps a case type enum value to its translation key and returns the translated string
const translateCaseType =
  (t: (key: string) => string) =>
  (caseType: ThirdPartyCaseTypes): string => {
    const caseTypeKeyMap: Record<ThirdPartyCaseTypes, string> = {
      bailiff: tKeys.THIRD_PARTY_CASES.CASE_TYPE.BAILIFF,
      debt_collector: tKeys.MODULES.AT_RISK.DEBT_COLLECTOR,
      lawyer: tKeys.THIRD_PARTY_CASES.CASE_TYPE.LAWYER,
    };
    return t(caseTypeKeyMap[caseType]);
  };

type SnapshotRowProps = {
  label: string;
  value: string;
  subtitle?: string;
};

const SnapshotRow = ({ label, value, subtitle }: SnapshotRowProps) => {
  const labelId = useId();

  return (
    <Group
      role="group"
      aria-labelledby={labelId}
      align="top"
      justify="space-between"
      py="sm"
      wrap="nowrap"
      gap="sm"
    >
      <Text id={labelId} size="sm">
        {label}
      </Text>
      <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
        <Text fw={700} size="sm">
          {value}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Stack>
    </Group>
  );
};
